import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { IFInstituicao, IFFinanciamento, IFHealthStatus, IFConvite } from '../types/financiamento.types';
import {
  fetchAllFinanciamentos,
  fetchContratosDisponiveis,
  fetchInstituicoes,
  createInstituicao,
  createFinanciamento,
  updateFinanciamentoStatus,
  fetchHealthCheck,
  fetchConvites,
  createConvite,
  resgatarConvite,
  revogarConvite,
  fetchConviteByToken,
  fetchContratoPreview,
  confirmarVinculo,
  suspenderVinculo,
  encerrarVinculo,
  fetchMeusContratos,
} from '../services/ifDataService';
import { supabase } from '@/integrations/supabase/client';

export type { IFInstituicao, IFFinanciamento, IFHealthStatus, IFConvite };

// ── Institutions ───────────────────────────────
export function useIFInstituicoes() {
  return useQuery({
    queryKey: ['if-instituicoes'],
    queryFn: async () => {
      const data = await fetchInstituicoes();

      // Enrich with contract counts
      const { data: fins } = await (supabase as any)
        .from('financiamento_contratos')
        .select('instituicao_id, status');

      const enriched = (data || []).map((inst: any) => {
        const related = (fins || []).filter((f: any) => f.instituicao_id === inst.id && f.status === 'ativo');
        return {
          ...inst,
          total_contratos: related.length,
        };
      });
      return enriched as IFInstituicao[];
    },
  });
}

export function useIFCriarInstituicao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInstituicao,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['if-instituicoes'] }),
  });
}

// ── Financiamentos (enriched with institution) ───────────────────────────────
export function useIFAdminFinanciamentos() {
  return useQuery({
    queryKey: ['if-admin-financiamentos'],
    queryFn: async () => {
      const data = await fetchAllFinanciamentos();

      const contratoIds = (data || []).map((d: any) => d.contrato_id);
      const instIds = (data || []).map((d: any) => d.instituicao_id).filter(Boolean);

      const [contratosRes, instsRes] = await Promise.all([
        contratoIds.length > 0
          ? (supabase as any).from('emp_contratos').select('id, nome').in('id', contratoIds)
          : { data: [] },
        instIds.length > 0
          ? (supabase as any).from('if_instituicoes').select('id, nome').in('id', instIds)
          : { data: [] },
      ]);

      const nameMap = new Map((contratosRes.data || []).map((c: any) => [c.id, c.nome]));
      const instMap = new Map((instsRes.data || []).map((i: any) => [i.id, i.nome]));

      return (data || []).map((d: any) => ({
        ...d,
        contrato_nome: nameMap.get(d.contrato_id) || '—',
        instituicao_nome: instMap.get(d.instituicao_id) || '—',
      })) as IFFinanciamento[];
    },
  });
}

export function useIFHealthCheck() {
  return useQuery({
    queryKey: ['if-health-check'],
    queryFn: async () => {
      return (await fetchHealthCheck()) as IFHealthStatus;
    },
    refetchInterval: 60_000,
  });
}

export function useIFContratosDisponiveis() {
  return useQuery({
    queryKey: ['if-contratos-disponiveis'],
    queryFn: async () => {
      return (await fetchContratosDisponiveis()) as { id: string; nome: string; contratada: string; status: string }[];
    },
  });
}

export function useIFCriarFinanciamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createFinanciamento,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-admin-financiamentos'] });
      qc.invalidateQueries({ queryKey: ['if-health-check'] });
      qc.invalidateQueries({ queryKey: ['if-instituicoes'] });
    },
  });
}

export function useIFAlterarStatusFinanciamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await updateFinanciamentoStatus(id, status);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-admin-financiamentos'] });
      qc.invalidateQueries({ queryKey: ['if-instituicoes'] });
    },
  });
}

// ── Convites ─────────────────────────────────────────────────────────────────

export function useIFConvites(contratoId?: string) {
  return useQuery({
    queryKey: ['if-convites', contratoId],
    queryFn: () => fetchConvites(contratoId),
  });
}

export function useIFCriarConvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createConvite,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-convites'] });
    },
  });
}

export function useIFResgatarConvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resgatarConvite,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-admin-financiamentos'] });
      qc.invalidateQueries({ queryKey: ['if-convites'] });
    },
  });
}

export function useIFRevogarConvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: revogarConvite,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-convites'] });
    },
  });
}

// ── Preview de convite por token ─────────────────────────────────────────────

export function useIFConviteByToken(token: string | null) {
  return useQuery({
    queryKey: ['if-convite-token', token],
    queryFn: () => fetchConviteByToken(token!),
    enabled: !!token && token.length >= 32,
  });
}

export function useIFContratoPreview(contratoId: string | null) {
  return useQuery({
    queryKey: ['if-contrato-preview', contratoId],
    queryFn: () => fetchContratoPreview(contratoId!),
    enabled: !!contratoId,
  });
}

// ── Governança de vínculos ───────────────────────────────────────────────────

export function useIFConfirmarVinculo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: confirmarVinculo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-admin-financiamentos'] });
      qc.invalidateQueries({ queryKey: ['if-meus-contratos'] });
    },
  });
}

export function useIFSuspenderVinculo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: suspenderVinculo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-admin-financiamentos'] });
      qc.invalidateQueries({ queryKey: ['if-meus-contratos'] });
    },
  });
}

export function useIFEncerrarVinculo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; motivo: string }) => encerrarVinculo(args.id, args.motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['if-admin-financiamentos'] });
      qc.invalidateQueries({ queryKey: ['if-meus-contratos'] });
    },
  });
}

// ── Meus contratos (IF user) ─────────────────────────────────────────────────

export function useIFMeusContratos() {
  return useQuery({
    queryKey: ['if-meus-contratos'],
    queryFn: fetchMeusContratos,
  });
}
